import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { AlgoliaCompanyRecord } from '../../types/company-search-record';
import { useFormatMessage } from '../../i18n-review/client/Msg';

const messages = defineMessages({
  heading: {
    id: 'companyProfile.ownedBrands.heading',
    defaultMessage: 'Owned brands',
    description: 'Heading of the company profile card listing sub-brands the company operates',
  },
});

interface OwnedBrandsCardProps {
  company: Pick<AlgoliaCompanyRecord, 'ownedBrands'>;
  className?: string;
}

/**
 * Sub-brands a company explicitly lists as its own (see CompanySiteProfile.ownedBrands).
 * Only ever renders human-approved brands — company.ownedBrands is populated exclusively
 * from CompanyData.ownedBrandsApproved (see generateCompanyScores.ts's assembleRecord),
 * never the raw LLM extraction, so no further gating is needed here.
 */
export function OwnedBrandsCard({ company, className = '' }: OwnedBrandsCardProps) {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const { ownedBrands } = company;
  if (!ownedBrands || ownedBrands.length === 0) return null;

  return (
    <div
      className={`rounded-2xl border border-[#DDD5C8] bg-white shadow-sm dark:border-[#4A3520] dark:bg-[#1F1509] p-4 sm:p-5 ${className}`}
    >
      <h2 className="text-base font-semibold text-[#1E1A14] dark:text-[#F2EADF]">
        {formatMessage(messages.heading)}
      </h2>
      <ul className="mt-3 flex flex-wrap gap-2">
        {ownedBrands.map((brand) => (
          <li key={brand.name}>
            {brand.url ? (
              <a
                href={brand.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block rounded-full border border-[#DDD5C8] px-3 py-1 text-sm text-[#1E1A14] underline-offset-2 hover:underline dark:border-[#4A3520] dark:text-[#F2EADF]"
              >
                {brand.name}
              </a>
            ) : (
              <span className="inline-block rounded-full border border-[#DDD5C8] px-3 py-1 text-sm text-[#1E1A14] dark:border-[#4A3520] dark:text-[#F2EADF]">
                {brand.name}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

OwnedBrandsCard.displayName = 'OwnedBrandsCard';
